import {
  Button,
  Checkbox,
  Col,
  Descriptions,
  Menu,
  Modal,
  notification,
  Result,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { Icon } from 'components';
import locale from 'locales';
import moment from 'moment';
import { ClientUpdateCheck } from 'pages';
import { connectByVdStart, getStateIconInfo, getXauthFromCookie, startAgent } from 'pages/utils';
import React, { useContext, useEffect, useState } from 'react';
import {
  AiFillApi,
  AiFillWindows,
  AiOutlineBars,
  AiOutlinePoweroff,
  AiOutlineQuestion,
  AiOutlineRedo,
  AiOutlineReload,
  AiOutlineSync,
  AiOutlineWarning,
} from 'react-icons/ai';
import {
  accountLogout,
  checkAccount,
  getConnHistory,
  getCustomClient,
  getCustomRedirect,
} from 'services/auth';
import { getAvailableVdDetail, getAvailableVdGroups, requestVdAction } from 'services/mgmt';
import { assignVd } from 'services/session';
import * as VsclientService from 'services/vsclient';
import ThemeContext from 'store/ThemeContext';
import styled from 'styled-components';
import packageJson from '../../../../package.json';
import monitor from '../../../images/monitor.png';

const StyledResult = styled(Result)`
  padding: 32px;
  height: 220px;
  .ant-result-icon {
    margin-bottom: 8px;
  }
`;

const VdMenu = styled(Menu)`
  &.ant-menu,
  .ant-menu-sub,
  &.ant-menu .ant-menu-sub {
    width: 200px;
    height: 200px;
    overflow-y: auto;
    background-color: #ecf1f6;
  }
`;

const VdInfo = styled.div`
  .monitor {
    position: relative;
    padding-top: 48px;
    width: 280px;
    height: 236px;
    background-image: url(${monitor});
    background-repeat: no-repeat;
  }
  .failoverBtn {
    position: absolute;
    right: 0;
    bottom: 0;
  }
  .ant-btn.ctaBtn {
    display: inline-flex;
    padding: 8px 40px;
    height: auto;
    border-color: rgba(255, 255, 255, 0.08);
    background-color: rgba(255, 255, 255, 0.16);
    color: #ffffff;
    font-weight: 500;
    &:hover,
    &:focus {
      color: #1890ff;
      border-color: #ffffff;
      background-color: #ffffff;
      box-shadow: 0 1.6px 3.6px 0 rgb(0 0 0 / 13%), 0 0.3px 0.9px 0 rgb(0 0 0 / 11%);
    }
    &[disabled] {
      background-color: rgba(0, 0, 0, 0.16);
      border-color: rgba(0, 0, 0, 0.32);
      opacity: 0.6;
      :hover {
        color: #ffffff;
        box-shadow: none;
      }
    }
  }
`;

const StyledBtn = styled(Button)`
  width: 156px;
  height: 56px;
  text-align: left;
  padding: 3px 24px;
  .anticon {
    font-size: 1.4em;
    vertical-align: middle;
  }
}
`;

function RenderTags({ status, groupType }) {
  const groupTypeTag = <Tag>{locale[groupType]}</Tag>;

  const color = getStateIconInfo(status);

  const statusTag = <Tag color={color}>{locale[status] || '알수없음'}</Tag>;
  return (
    <div style={{ display: 'flex' }}>
      {groupTypeTag} {statusTag}
    </div>
  );
}

/**
 * 접속 가능한 VD 목록
 * @param {object} history 다운로드 페이지 이동을 위함
 * @returns ReactNode
 */
function AvailableGroupVds({ refresh, setVdsRefresh, onRefresh, redirectUrlToVd }) {
  const [vdGroups, setVdGroups] = useState([]);
  const [selectedVdGroup, setSelectedVdGroup] = useState(undefined);
  const [isError, setIsError] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [useMultiMonitor, setUseMultiMonitor] = useState(false);
  const [connHistory, setConnHistory] = useState([]);
  const [visible, setVisible] = useState(false);
  const [reloading, setReloading] = useState(true);
  const [customUrls, setCustomUrls] = useState();
  const [stateSyncId, setStateSyncId] = useState();
  const theme = useContext(ThemeContext);

  useEffect(() => {
    if (stateSyncId) {
      clearInterval(stateSyncId);
    }

    // 주기적으로 VD 상태 업데이트
    const id = setInterval(async () => {
      try {
        const res = await getAvailableVdDetail(selectedVdGroup?.id);
        if (res.data) {
          setSelectedVdGroup(res.data);
        }
      } catch (e) {
        clearInterval(id);
        setStateSyncId(null);
      }
    }, 10 * 1000);

    setStateSyncId(id);
  }, [selectedVdGroup]);
  /**
   * VD 접속하기
   *  - vsclient 설치 여부 확인한 후
   *  - Protocol Handler를 사용하여 vsclient 앱을 실행한다.
   *  - 이때 vsclient에 로그인 정보를 전달한다.(vsclient가 vsmgmt에게 connection 정보를 받아오기 위해 auth token이 필요하다. Cookie에서 parsing 해온다.)
   * TODO
   *  - 에이전트 미설치시 경고 필요: window.blur 이벤트로는 감지 못해 롤백함, qa-management#182
   */
  const connect = async (vd) => {
    const token = getXauthFromCookie();

    const vdGroup = vd || selectedVdGroup;

    if (token) {
      // 토큰은 존재하나 패스워드가 변경되어 인증에 실패한 경우를 처리
      try {
        await checkAccount(token);
      } catch (e) {
        Modal.warning({
          title: '인증 실패',
          content: '로그인 세션이 만료되었거나 패스워드가 변경되었을 수 있습니다.',
          onOk: async () => {
            await accountLogout();
          },
        });
        return;
      }

      const searchParam = {
        'conn-req-url': `${window.location.protocol}//${window.location.host}/portal/api/session/vdgroups/${vdGroup.id}/connection`,
        'conn-token': token,
        username: localStorage.getItem('username'),
        ...(useMultiMonitor && { 'multi-monitors': true }),
      };

      if (redirectUrlToVd && localStorage.getItem('redirectComplete') !== 'true') {
        searchParam['redirect-url'] = redirectUrlToVd;
      }

      // vsclient에게 넘겨줄 args
      const args = new URLSearchParams(searchParam).toString();

      try {
        if (redirectUrlToVd && localStorage.getItem('redirectComplete') !== 'true') {
          await VsclientService.redirectUrl(args);
          localStorage.setItem('redirectComplete', true);
          return;
        }

        await VsclientService.start(args);
      } catch (e) {
        const agt = await startAgent(2);

        if (
          theme?.protocolHandler &&
          !redirectUrlToVd &&
          localStorage.getItem('redirectComplete') !== 'true'
        ) {
          window.location = `VdiVsclient:${btoa(args)}`;
          return;
        }

        // ie 버전에서 axios request가 액세스가 거부되었습니다 에러가 난 경우 window open으로 처리
        const url =
          redirectUrlToVd && localStorage.getItem('redirectComplete') !== 'true'
            ? `http://localhost:61811/vsclient/redirect-url?args=${btoa(args)}`
            : `http://localhost:61811/vsclient/start?args=${btoa(args)}`;

        if (agt) {
          const ret = window.open(url);
          if (!ret) {
            Modal.warning({
              title: 'VD 접속 실패',
              content: '에이전트 실행 버튼을 누른 후 다시 시도 하십시오',
            });
            return;
          }

          setTimeout(() => {
            ret.close();
          }, 3 * 1000);
        }
      }
    } else {
      Modal.error({
        title: 'VD 접속 실패',
        content: '로그인 정보가 없습니다.',
      });
    }
  };

  const vdAction = async (action) => {
    const { data } = await requestVdAction(selectedVdGroup.id, action);
    const createMarkup = (value) => {
      return { __html: value };
    };
    if (data.status === 'error') {
      Modal.error({
        title: '작업 실패',
        content: data.message,
      });
    } else {
      Modal.success({
        title: '작업 성공',
        content: (
          <Typography.Text style={{ fontSize: 16 }}>
            <span
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={createMarkup(locale[data.message])}
            />
          </Typography.Text>
        ),
        onOk: () => {
          setReloading(true);
        },
      });
    }
  };

  /**
   * 장애 조치가 불가능한 상태인지 체크한다.
   * https://gitlab.somansa.com/vdis/vsportal/-/wikis/Knowledge-Base#portal%EC%97%90%EC%84%9C-%EC%9E%A5%EC%95%A0-%EC%A1%B0%EC%B9%98%EA%B0%80-%EB%B6%88%EA%B0%80%ED%95%9C-%EC%83%81%ED%99%A9
   * @param {string} action 장애 조치 키를 의미하며 theme.failover에 정의되어 있음 + 접속 키
   * @param {object} vd VD 정보
   * @returns true(불가) | false(가능)
   */
  const disabledFailover = (action, vd) => {
    const { user, state, subState } = vd;
    let result;
    switch (action) {
      case 'connect':
        result = !(
          state?.endsWith('.state.ready') ||
          state?.endsWith('.state.idle') ||
          state?.endsWith('.state.connected') ||
          state?.endsWith('.state.disconnected') ||
          state?.endsWith('.state.stopped')
        );
        break;
      case 'start':
        if (!user) {
          result = true;
        } else {
          result = !state.endsWith('.state.stopped') || subState.endsWith('.sub-state.migrating');
        }
        break;
      case 'soft-reboot':
        if (!user) {
          result = true;
        } else {
          result =
            !(
              state.endsWith('.state.ready') ||
              state.endsWith('.state.idle') ||
              // state.endsWith('.state.connected') ||
              state.endsWith('.state.disconnected')
            ) || subState.endsWith('.sub-state.migrating');
        }
        break;
      case 'hard-reboot':
        if (!user) {
          result = true;
        } else {
          result =
            !(
              state.endsWith('.state.ready') ||
              state.endsWith('.state.idle') ||
              state.endsWith('.state.disconnected') ||
              state.endsWith('.state.stopping') ||
              state.endsWith('.state.stopped') ||
              // state.endsWith('.state.connected') ||
              // state.endsWith('.state.booting') ||
              // state.endsWith('.state.rebooting') ||
              state.endsWith('.state.error')
            ) || subState.endsWith('.sub-state.migrating');
        }
        break;
      case 'remoteHelpService':
        result = false;
        break;
      default:
        result = false;
        break;
    }
    return result;
  };

  const autoConnect = async (vdData, endTime) => {
    if (!vdData) {
      return;
    }

    const isAutoConnectSet = await getCustomClient();

    if (localStorage.getItem('autoConnect') === 'true' && isAutoConnectSet) {
      // 접속 시도 중
      localStorage.setItem('autoConnect', false);
      if (!vdData.vdName) {
        // VD에 할당되어있지 않은 경우
        // 1. 할당해라
        // 2. 접속해라
        const vd = (await assignVd(vdData.id)).data;
        if (vd.state.endsWith('stopped')) {
          connectByVdStart(vd, moment().add(3, 'm'), (groupInfo) => connect(groupInfo));
        } else {
          connect(vd);
        }

        return;
      }

      if (vdData.state === 'vd.state.stopped') {
        connectByVdStart(vdData, endTime, (groupInfo) => connect(groupInfo));
        // 10초에 한 번씩 get 날려서 VD 상태가 접속 가능한 상태인지 파악
        // 접속 가능해지면 바로 연결
        // 3분이 지나도 연결 가능한 상태가 아니라면 접속 실패 모달 띄움
      }
      if (!disabledFailover('connect', vdData)) {
        connect(vdData);
      }
    }
    // const tes = ;
  };

  useEffect(() => {
    const fetchAvailableVdGroups = async () => {
      setIsError(false);
      setIsLoading(true);
      const res = await getAvailableVdGroups();

      if (res.status !== 'error' && res.data.length === 0) {
        notification.warning({
          message: 'Desktop 자동 접속 실패',
          description: '가용한 Desktop이 없습니다.',
        });
        setIsLoading(false);
        return;
      }

      if (res.status === 'error') {
        notification.warning({
          message: 'Desktop 자동 접속 실패',
          description: res.errorMsg,
        });
        setIsLoading(false);
        return;
      }

      let index = -1;
      res.data.forEach((d, idx) => {
        if (d.autoLogin === true) {
          index = idx;
        }
      });

      try {
        setVdGroups(res.data);
        if (!selectedVdGroup) {
          setSelectedVdGroup(res.data[index === -1 ? 0 : index]);
          // 자동 로그인 시도 만약 꺼져있으면 3분 동안 켜지길 기다림
          if (index !== -1) {
            // eslint-disable-next-line no-unused-expressions
            await autoConnect(res.data[index], moment().add(3, 'm'));
          } else {
            notification.warning({
              message: 'Desktop 자동 접속 실패',
              description: '내정보에서 자동 접속 설정을 확인하십시오.',
            });
          }
        } else {
          setSelectedVdGroup((prevState) =>
            res.data.find((vdGroup) => prevState.id === vdGroup.id),
          );
        }
      } catch (err) {
        setIsError(true);
        setErrorMsg(err);
      }
      setIsLoading(false);
    };
    if (reloading) {
      fetchAvailableVdGroups();
      setReloading(false);
    }

    if (refresh) {
      fetchAvailableVdGroups();
      onRefresh();
    }
  }, [reloading, refresh]);

  // 최근 접속일을 가져온다. 최근 접속일은 selectedVdGroup에 의존한다.
  useEffect(async () => {
    setConnHistory(
      (await getConnHistory(selectedVdGroup?.id)).map((item) => ({
        ...item,
        loginDate: moment(item.loginDate).format('YYYY-MM-DD HH:mm:ss'),
        logoutDate: item.logoutDate ? moment(item.logoutDate).format('YYYY-MM-DD HH:mm:ss') : '-',
      })),
    );
  }, [selectedVdGroup?.id]);

  useEffect(() => {
    getCustomRedirect().then((customUrl) => setCustomUrls(customUrl));
  }, []);

  // 자가 조치 컴포넌트
  function Failovers() {
    const result = Object.entries(theme.failover).map(([key, value]) => {
      let icon;
      let title;
      switch (key) {
        case 'start':
          icon = <AiOutlinePoweroff />;
          break;
        case 'soft-reboot':
          icon = <AiOutlineRedo />;
          title =
            'Desktop 접속이 불가능한 경우 문서의 유실 없이 오류 복구를 할 수 있습니다. 만약 서비스 재부팅 이후에 오류 복구가 되지 않는다면 Desktop 재부팅 기능을 이용해주시기 바랍니다.';
          break;
        case 'hard-reboot':
          icon = <AiOutlineSync />;
          title = `Desktop 재부팅을 시도하여 오류 복구를 할 수 있습니다.${
            packageJson.config?.preid === 'hanabank'
              ? ' 만약 재부팅 이후에도 Desktop의 오류 복구가 되지 않는다면 원격지원 서비스로 문의하시기 바랍니다.'
              : ' '
          }`;
          break;
        case 'remoteHelpService':
          icon = <AiOutlineWarning />;
          break;
        default:
          icon = <AiOutlineQuestion />;
          break;
      }

      return (
        <Tooltip key={key} title={title} overlayInnerStyle={{ wordBreak: 'keep-all' }}>
          <StyledBtn
            icon={<Icon name={icon} />}
            disabled={disabledFailover(key, selectedVdGroup)}
            onClick={() =>
              key === 'remoteHelpService'
                ? window.open(customUrls?.remoteHelpService, '', '')
                : Modal.confirm({
                    title: `정말로 ${value.text} 하시겠습니까? `,
                    content:
                      '이 작업은 오래 걸릴 수 있습니다. 새로고침으로 반영된 상태를 체크하십시오.',
                    onOk() {
                      vdAction(key);
                    },
                  })
            }
          >
            {value.text}
          </StyledBtn>
        </Tooltip>
      );
    });

    return (
      <Space direction="vertical" size={4}>
        {result}
      </Space>
    );
  }

  return (
    <div>
      <ClientUpdateCheck />
      {isError ? (
        <StyledResult
          status="warning"
          title={
            errorMsg ? (
              `${errorMsg}`
            ) : (
              <div style={{ fontSize: 16 }}>
                예상치 못한 에러가 발생했습니다.
                <br />
                잠시 후 다시 이용해주십시오.
              </div>
            )
          }
        />
      ) : (
        <Spin spinning={isLoading}>
          {vdGroups.length === 0 ? (
            <StyledResult
              status="info"
              title={<div style={{ fontSize: 16 }}>가용한 Desktop이 없습니다.</div>}
            />
          ) : (
            <Row gutter={[16, 0]} style={{ flexWrap: 'nowrap' }}>
              <Col flex="none">
                <Space style={{ marginBottom: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 500 }}>접속 가능 Desktops</span>
                  <Tooltip title="새로고침">
                    <Button
                      type="text"
                      icon={
                        // 버튼내 아이콘만 있는 경우 좌우 정렬이 되지않는 이슈가 있어 span을 추가함
                        <span>
                          <AiOutlineReload className="anticon" />
                        </span>
                      }
                      onClick={() => setVdsRefresh(true)}
                    />
                  </Tooltip>
                </Space>
                <VdMenu defaultSelectedKeys={vdGroups[0].id} selectedKeys={selectedVdGroup?.id}>
                  {vdGroups.map((vdGroup) => (
                    <Menu.Item
                      key={vdGroup.id}
                      onClick={async () => {
                        setConnHistory(
                          (await getConnHistory(vdGroup.id)).map((item) => ({
                            ...item,
                            loginDate: moment(item.loginDate).format('YYYY-MM-DD HH:mm:ss'),
                            logoutDate: item.logoutDate
                              ? moment(item.logoutDate).format('YYYY-MM-DD HH:mm:ss')
                              : '-',
                          })),
                        );
                        setSelectedVdGroup(vdGroup);
                      }}
                    >
                      {vdGroup.vdGroupName}
                    </Menu.Item>
                  ))}
                </VdMenu>
              </Col>
              <Col flex="auto">
                <VdInfo>
                  {selectedVdGroup ? (
                    <Row gutter={[20, 8]} align="middle">
                      <Col flex="240px">
                        <Typography.Title level={4} ellipsis>
                          <Space direction="vertical" size={4}>
                            <RenderTags
                              status={selectedVdGroup.state}
                              groupType={selectedVdGroup.groupType}
                            />
                            <Space align="center">
                              <AiFillWindows className="anticon" fontSize={24} />
                              <Typography.Text ellipsis style={{ width: 200 }}>
                                {selectedVdGroup.vdName || selectedVdGroup.vdGroupName}
                              </Typography.Text>
                            </Space>
                          </Space>
                        </Typography.Title>
                        <Descriptions
                          column={1}
                          style={{ marginTop: 16 }}
                          labelStyle={{ padding: '1px 0' }}
                          contentStyle={{ padding: '1px 0' }}
                        >
                          <Descriptions.Item label="OS">{selectedVdGroup.os}</Descriptions.Item>
                          <Descriptions.Item label="CPU">
                            {selectedVdGroup.cpus}core
                          </Descriptions.Item>
                          <Descriptions.Item label="Memory">
                            {selectedVdGroup.ram}GB
                          </Descriptions.Item>
                          <Descriptions.Item label="Disk">
                            {selectedVdGroup.size}GB
                          </Descriptions.Item>
                          <Descriptions.Item label="IP">
                            {selectedVdGroup.ip || '가상 데스크탑 할당 전'}
                          </Descriptions.Item>
                          <Descriptions.Item label="최근 접속일">
                            <Space align="start">
                              <span>
                                {connHistory.length ? connHistory[0].loginDate : '접속 기록 없음'}
                              </span>
                              <Button
                                size="small"
                                style={{ position: 'relative', top: -3 }}
                                icon={
                                  <span>
                                    <Tooltip title="최근 접속 이력">
                                      <AiOutlineBars className="anticon" />
                                    </Tooltip>
                                  </span>
                                }
                                onClick={async () => {
                                  setVisible(true);
                                }}
                              />
                              <Modal
                                title="최근 접속 이력"
                                visible={visible}
                                onOk={() => {
                                  setVisible(false);
                                }}
                                onCancel={() => {
                                  setVisible(false);
                                }}
                                cancelButtonProps={{ style: { display: 'none' } }}
                              >
                                <Table
                                  size="small"
                                  rowKey="sessionId"
                                  dataSource={connHistory}
                                  columns={[
                                    { title: '접속 일자', dataIndex: 'loginDate' },
                                    {
                                      title: '접속 종료 일자',
                                      dataIndex: 'logoutDate',
                                    },
                                    { title: '접속 단말 IP', dataIndex: 'ip' },
                                  ]}
                                />
                              </Modal>
                            </Space>
                          </Descriptions.Item>
                        </Descriptions>
                      </Col>
                      <Col flex="280px">
                        <div className="monitor">
                          <Space
                            direction="vertical"
                            size="large"
                            style={{ display: 'flex', width: '100%', alignItems: 'center' }}
                          >
                            <Button
                              size="large"
                              className="ctaBtn"
                              icon={<AiFillApi className="anticon" fontSize={24} />}
                              onClick={async () => {
                                if (disabledFailover('connect', selectedVdGroup)) {
                                  Modal.error({
                                    title: 'VD 접속 실패',
                                    content: '현재 VD 가 접속 가능한 상태가 아닙니다.',
                                  });
                                } else if (!selectedVdGroup.vdName) {
                                  // VD에 할당되어있지 않은 경우
                                  // 1. 할당해라
                                  // 2. 접속해라
                                  const vd = (await assignVd(selectedVdGroup.id)).data;

                                  // eslint-disable-next-line no-unused-expressions
                                  vd.state.endsWith('stopped')
                                    ? connectByVdStart(vd, moment().add(3, 'm'), (groupInfo) =>
                                        connect(groupInfo),
                                      )
                                    : connect(vd);
                                } else {
                                  // eslint-disable-next-line no-unused-expressions
                                  selectedVdGroup.state.endsWith('.state.stopped')
                                    ? connectByVdStart(
                                        selectedVdGroup,
                                        moment().add(3, 'm'),
                                        (groupInfo) => connect(groupInfo),
                                      )
                                    : connect();
                                }
                              }}
                              disabled={disabledFailover('connect', selectedVdGroup)}
                            >
                              접속
                            </Button>
                            <Checkbox
                              style={{ color: '#ffffff' }}
                              onChange={(e) => setUseMultiMonitor(e.target.checked)}
                            >
                              멀티 모니터 사용
                            </Checkbox>
                          </Space>
                        </div>
                      </Col>
                      <Col style={{ alignSelf: 'flex-start' }}>
                        <Failovers />
                      </Col>
                    </Row>
                  ) : (
                    <div>왼쪽 목록에서 Desktop을 선택하세요.</div>
                  )}
                </VdInfo>
              </Col>
            </Row>
          )}
        </Spin>
      )}
    </div>
  );
}

export default AvailableGroupVds;
